import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@codes/ui-kit/components/ui/sheet';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

import type { CreateKeyDTO, KeyView } from '@codes/codes-keys';
import { useCodesKeyDetail, useCreateCodesKey, useUpdateCodesKey } from '@codes/codes-keys';
import { isLocalEnvironment } from '@codes/web-core';

import { CodesKeyEditFields } from './CodesKeyEditFields';

interface CodesKeyFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
    keyData: KeyView | null;
    onSuccess: (key?: { apiKey: string; name: string }) => void;
}

type FormErrors = {
    name?: string;
};

export const CodesKeyFormDialog = ({ open, onOpenChange, mode, keyData, onSuccess }: CodesKeyFormDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const createMutation = useCreateCodesKey();
    const updateMutation = useUpdateCodesKey();

    const { data: detailData, isLoading: isLoadingDetail } = useCodesKeyDetail(keyData?.id || '', {
        enabled: mode === 'edit' && open && !!keyData?.id,
    });

    const formData = mode === 'edit' ? detailData || keyData : null;
    const [name, setName] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [mocks, setMocks] = useState(isLocalEnvironment());
    const [hidden, setHidden] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    useEffect(() => {
        if (open) {
            if (mode === 'edit' && formData) {
                setName(formData.name ?? '');
                setHidden(formData.hidden ?? false);
                setInvalid(formData.invalid ?? false);
            } else if (mode === 'create') {
                setName('');
                setApiKey('');
                setMocks(isLocalEnvironment());
                setHidden(false);
                setInvalid(false);
            }
            setErrors({});
        }
    }, [open, mode, formData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (mode === 'create' && !name.trim()) {
            newErrors.name = t('codesKeys.form.nameRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            if (mode === 'create') {
                const body: CreateKeyDTO = {
                    name: name.trim() || undefined,
                    apiKey: apiKey.trim() || undefined,
                };

                const result = await createMutation.mutateAsync({ body, mocks });

                if (!result.apiKey) {
                    throw new Error(t('codesKeys.form.apiKeyNotReturned'));
                }

                toast({
                    title: t('codesKeys.createSuccess'),
                });
                onOpenChange(false);
                onSuccess({ apiKey: result.apiKey, name: name.trim() || 'codes-key' });
            } else if (mode === 'edit' && keyData?.id) {
                const body = {
                    name: name.trim() || undefined,
                    hidden,
                    invalid,
                };

                await updateMutation.mutateAsync({ id: keyData.id, body });
                toast({
                    title: t('codesKeys.updateSuccess'),
                });
                onOpenChange(false);
                onSuccess();
            }
        } catch (error) {
            toast({
                title: t('codesKeys.error'),
                description: error instanceof Error ? error.message : t('codesKeys.form.unknownError'),
                variant: 'destructive',
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    if (mode === 'edit' && isLoadingDetail) {
        return (
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[540px]">
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[540px]">
                <SheetHeader aria-describedby={undefined}>
                    <SheetTitle className="flex items-center gap-2">
                        {mode === 'create' ? t('codesKeys.createNew') : t('codesKeys.form.edit')}
                        <Badge variant="success">Beta</Badge>
                    </SheetTitle>
                </SheetHeader>

                <div className="flex flex-col gap-y-6 py-6">
                    <div className="text-sub">
                        {mode === 'create'
                            ? t('codesKeys.form.createDescription')
                            : t('codesKeys.form.editDescription')}
                    </div>

                    <form onSubmit={onSubmit} className="flex flex-col gap-y-6">
                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="name">
                                {t('codesKeys.form.nameLabel')} {mode === 'create' && '*'}
                            </Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder={t('codesKeys.form.namePlaceholder')}
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                            <p className="text-xs text-sub">{t('codesKeys.form.nameDescription')}</p>
                        </div>

                        {mode === 'create' && isLocalEnvironment() && (
                            <>
                                <div className="flex flex-col gap-y-2">
                                    <Label htmlFor="apiKey">{t('codesKeys.form.apiKeyOptionalLabel')}</Label>
                                    <Input
                                        id="apiKey"
                                        value={apiKey}
                                        onChange={e => setApiKey(e.target.value)}
                                        placeholder={t('codesKeys.form.apiKeyPlaceholder')}
                                        disabled={isLoading}
                                        autoComplete="off"
                                    />
                                    <p className="text-xs text-sub">{t('codesKeys.form.apiKeyDescription')}</p>
                                </div>

                                <div className="flex flex-col gap-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="mocks"
                                            type="checkbox"
                                            checked={mocks}
                                            onChange={e => setMocks(e.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4"
                                        />
                                        <Label htmlFor="mocks" className="cursor-pointer">
                                            {t('codesKeys.form.useMocks')}
                                        </Label>
                                    </div>
                                    <p className="text-xs text-sub">{t('codesKeys.form.useMocksDescription')}</p>
                                </div>
                            </>
                        )}

                        {mode === 'edit' && (
                            <CodesKeyEditFields
                                formData={formData}
                                hidden={hidden}
                                onHiddenChange={setHidden}
                                invalid={invalid}
                                onInvalidChange={setInvalid}
                                isLoading={isLoading}
                            />
                        )}

                        <SheetFooter className="pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('codesKeys.deleteDialog.cancel')}
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {mode === 'create' ? t('codesKeys.generate') : t('codesKeys.update')}
                            </Button>
                        </SheetFooter>
                    </form>
                </div>
            </SheetContent>
        </Sheet>
    );
};
